import React, { useState, useMemo } from "react";
import { Users, Search, ChevronLeft, ChevronRight, Check, UserX } from "lucide-react";
import { getUserDisplayName } from "../utils/calendarHelpers";
import "../styles/CalendarCollaboratorsSidebar.css";

/**
 * CalendarCollaboratorsSidebar
 * Sidebar gauche pour la sélection de collaborateurs dans CalendarPage (Formation).
 *
 * Props :
 * - collaborators : tableau de directReports
 * - selectedCollaborator : "" (toute l'équipe) | "id" (collaborateur)
 * - onSelectCollaborator : (value) => void
 * - loading : boolean
 */
const CalendarCollaboratorsSidebar = ({
    collaborators = [],
    selectedCollaborator = "",
    onSelectCollaborator,
    loading = false,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    // === HELPERS ===
    const getInitials = (collab) => {
        const first = collab?.firstName?.charAt(0) || collab?.prenom?.charAt(0) || "";
        const last = collab?.lastName?.charAt(0) || collab?.nom?.charAt(0) || "";
        if (first || last) return `${first}${last}`.toUpperCase();
        if (collab?.username) return collab.username.charAt(0).toUpperCase();
        return "?";
    };

    const getSubtitle = (collab) => {
        return collab?.email || collab?.username || collab?.matricule || collab?.employeeCode || "-";
    };

    // Color palette for avatar backgrounds
    const avatarColors = [
        "linear-gradient(135deg, #3a5a9c, #5b7db8)",
        "linear-gradient(135deg, #4a6fa0, #6c8ebf)",
        "linear-gradient(135deg, #5a80aa, #7b9cc8)",
        "linear-gradient(135deg, #354d7a, #4e6e9e)",
        "linear-gradient(135deg, #3d5d8f, #5a7aab)",
        "linear-gradient(135deg, #4b6c98, #6889b5)",
        "linear-gradient(135deg, #587aa3, #7696c0)",
        "linear-gradient(135deg, #2c4a72, #3f6290)",
    ];

    const getAvatarColor = (id) => {
        const index = typeof id === "number" ? id : (String(id).charCodeAt(0) || 0);
        return avatarColors[index % avatarColors.length];
    };

    // === SEARCH FILTER ===
    const filteredCollaborators = useMemo(() => {
        if (!searchTerm.trim()) return collaborators;
        const term = searchTerm.toLowerCase().trim();
        return collaborators.filter((c) => {
            const displayName = getUserDisplayName(c).toLowerCase();
            const haystack = [
                displayName,
                c?.firstName || c?.prenom || "",
                c?.lastName || c?.nom || "",
                c?.email || "",
                c?.username || "",
                c?.matricule || "",
                c?.employeeCode || "",
                c?.entityName || "",
            ]
                .join(" ")
                .toLowerCase();
            return haystack.includes(term);
        });
    }, [collaborators, searchTerm]);

    // === PAGINATION ===
    const totalItems = filteredCollaborators.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);
    const paginatedCollaborators = filteredCollaborators.slice(startIndex, endIndex);
    const showPagination = totalItems > pageSize;

    // === HANDLERS ===
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleSelect = (value) => {
        if (onSelectCollaborator) {
            onSelectCollaborator(value);
        }
    };

    // === RENDER ===
    return (
        <div className="formation-team-sidebar">
            {/* Header */}
            <div className="formation-team-sidebar-header">
                <h3 className="formation-team-sidebar-title">
                    <Users size={18} />
                    Équipe manager
                </h3>
                <p className="formation-team-sidebar-count">
                    {collaborators.length} collaborateur{collaborators.length !== 1 ? "s" : ""}
                </p>
            </div>

            {/* Search */}
            <div className="formation-team-search-wrapper">
                <div className="formation-team-search-container">
                    <Search className="formation-team-search-icon" />
                    <input
                        type="text"
                        className="formation-team-search"
                        placeholder="Rechercher un collaborateur..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="formation-team-loading">Chargement...</div>
            )}

            {/* User list */}
            {!loading && (
                <div className="formation-team-user-list">
                    {/* Card "Toute l'équipe" — always visible */}
                    <button
                        type="button"
                        className={`formation-team-user-card formation-team-all-card ${
                            !selectedCollaborator ? "formation-team-user-card-selected" : ""
                        }`}
                        onClick={() => handleSelect("")}
                    >
                        <div className="formation-team-avatar">
                            <div
                                className="formation-team-avatar-initials"
                                style={{ background: "linear-gradient(135deg, var(--calendar-navy, #0e2046), #2a4a8a)" }}
                            >
                                <Users size={18} />
                            </div>
                        </div>
                        <div className="formation-team-user-info">
                            <span className="formation-team-user-name">Toute l&apos;équipe</span>
                            <span className="formation-team-user-subtitle">
                                Tous les événements de vos collaborateurs
                            </span>
                        </div>
                        <div className="formation-team-select-indicator">
                            <Check size={13} />
                        </div>
                    </button>

                    {/* Collaborator cards */}
                    {paginatedCollaborators.length > 0 ? (
                        paginatedCollaborators.map((collab) => {
                            const isSelected = Number(selectedCollaborator) === Number(collab?.id);
                            const displayName = getUserDisplayName(collab);
                            const subtitle = getSubtitle(collab);
                            const initials = getInitials(collab);

                            return (
                                <button
                                    key={collab?.id}
                                    type="button"
                                    className={`formation-team-user-card ${isSelected ? "formation-team-user-card-selected" : ""}`}
                                    onClick={() => handleSelect(String(collab?.id))}
                                >
                                    <div className="formation-team-avatar">
                                        {collab?.profilePictureUrl ? (
                                            <img
                                                src={collab.profilePictureUrl}
                                                alt={displayName}
                                                className="formation-team-avatar-img"
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                    if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                                                }}
                                            />
                                        ) : null}
                                        <div
                                            className="formation-team-avatar-initials"
                                            style={{
                                                background: getAvatarColor(collab?.id),
                                                display: collab?.profilePictureUrl ? "none" : "flex",
                                            }}
                                        >
                                            {initials}
                                        </div>
                                    </div>

                                    <div className="formation-team-user-info">
                                        <span className="formation-team-user-name">{displayName}</span>
                                        <span className="formation-team-user-subtitle">{subtitle}</span>
                                    </div>

                                    <div className="formation-team-select-indicator">
                                        <Check size={13} />
                                    </div>
                                </button>
                            );
                        })
                    ) : (
                        <div className="formation-team-empty">
                            <div className="formation-team-empty-icon">
                                <UserX size={22} />
                            </div>
                            {searchTerm.trim()
                                ? "Aucun collaborateur trouvé"
                                : "Aucun collaborateur dans l'équipe"
                            }
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {!loading && showPagination && (
                <div className="formation-team-pagination">
                    <div className="formation-team-pagination-row">
                        <span className="formation-team-pagination-info">
                            {startIndex + 1} – {endIndex} sur {totalItems}
                        </span>
                        <div className="formation-team-pagination-controls">
                            <button
                                type="button"
                                className="formation-team-pagination-button"
                                disabled={safePage <= 1}
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                title="Page précédente"
                            >
                                <ChevronLeft size={15} />
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    type="button"
                                    className={`formation-team-pagination-button ${page === safePage ? "is-active" : ""}`}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            ))}

                            <button
                                type="button"
                                className="formation-team-pagination-button"
                                disabled={safePage >= totalPages}
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                title="Page suivante"
                            >
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>

                    <div className="formation-team-pagination-row">
                        <div className="formation-team-page-size">
                            <span className="formation-team-page-size-label">Par page :</span>
                            <select value={pageSize} onChange={handlePageSizeChange}>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarCollaboratorsSidebar;
